/**
 * formatMessage — shared markdown-ish -> HTML converter for Dashboard5.
 * Ported from TSEDashboard2.formatBotMessage(). Kept as a plain function
 * (not a hook) so it can be called incrementally on growing streamed text
 * without violating hook rules.
 */

// Shown when an image link carries no label of its own. Kept identical to the
// text the server emits (app/utils.py) so a labelled link and a fallback one
// read the same in the transcript.
const IMAGE_LINK_LABEL = "View image";

const isImagePreviewLink = (label = "", url = "") => {
  const l = label.trim().toLowerCase();
  const u = url.trim().toLowerCase();
  return (
    l.includes("image") || l.includes("screenshot") ||
    /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(u) ||
    u.includes("drive.google.com/file/d/")
  );
};

const isServerProxyDocUrl = (url = "") => {
  if (!url) return false;
  try {
    const p = new URL(url, window.location.origin).pathname;
    return p.includes("/api/images/") || p.includes("/api/documents/");
  } catch {
    return /\/api\/(images|documents)\//.test(url);
  }
};

// Raw HTML in the text is never trusted: every producer (LLM answers, runbook node text, narration template
// slots, image captions) is escaped before the markdown transforms build the only HTML this renders. `<br>`
// is the one tag runbook authors use (title / body separator in node text), so it is kept as a line break.
// The apostrophe is left alone: every attribute built here is double-quoted.
const BR_TAG_RE = /<br\s*\/?>/gi;
const escapeProse = (s) => String(s)
  .replace(BR_TAG_RE, "\n")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;");
// Link targets allowed in href / data-* attributes: absolute http(s), or the server's relative proxy paths.
// Anything else (javascript:, data:, vbscript:, …) never matches a link rule and stays plain text.
const SAFE_URL = "(?:https?:\\/\\/[^)]+|\\/api\\/(?:images|documents)\\/[^)\\s]+)";
const MD_IMAGE_RE = new RegExp(`!\\[([^\\]]*)\\]\\((${SAFE_URL})\\)`, "g");
const MD_LINK_RE = new RegExp(`\\[([^\\]]+)\\]\\((${SAFE_URL})\\)`, "g");

// The agent's ::sources … ::end manifest is parsed server-side into source
// cards and stripped before streaming. Strip it again here so a manifest that
// arrives by any other path (cached answer, REST replay) never renders as text.
const MANIFEST_RE = /^[ \t]*::sources[ \t]*\r?\n[\s\S]*?^[ \t]*::end[ \t]*\r?\n?/gim;

/**
 * Same manifest strip as the renderer, for consumers that need plain text
 * rather than HTML — the .md export, which was writing the raw "::sources /
 * S1 | why / ::end" block into a file an engineer attaches to a case.
 */
export function stripManifest(text) {
  if (!text) return "";
  const t = String(text).replace(MANIFEST_RE, "");
  // Unterminated block (message cut off mid-manifest): drop to end of string,
  // but never when that erases everything — see formatBotMessage's note.
  const withoutTail = t.replace(/^[ \t]*::sources[ \t]*\r?\n[\s\S]*$/im, "");
  if (withoutTail.trim()) return withoutTail.trim();
  return t
    .replace(/^[ \t]*::(?:sources|end)[ \t]*$/gim, "")
    .replace(/^[ \t]*S\d+[ \t]*\|.*$/gim, "")
    .trim();
}


// The grounding check appends this to a sentence whose citation it removed.
// Internal QA signal, same as the pills — see grounding_check.py.
const GROUNDING_MARKER_RE = /\s*\(unverified: not found in the cited source\)/gi;

/**
 * The answer with every INTERNAL citation artefact removed.
 *
 * "Send to customer" puts an engineer-facing answer in front of the customer,
 * and the citation apparatus is not for them: [S1] pills are numbers that only
 * mean something beside the source cards, which the customer cannot see, and
 * the ::sources manifest and the unverified marker are internal QA signals. A
 * customer reading "restart the connector [S2]" sees a typo or a reference to
 * something missing; either way it costs trust the answer was meant to build.
 *
 * Only the markers go. The prose is untouched — this strips attribution, it
 * does not sanitise or rewrite what was said.
 */
export function stripCitations(text) {
  if (!text) return "";
  return stripManifest(String(text))
    .replace(GROUNDING_MARKER_RE, "")
    .replace(CITE_RE, "")
    // ONLY links into THIS deployment go. Those are citation plumbing: a
    // source PDF or a proxied screenshot behind the engineer's auth, labelled
    // with an internal filename and a content hash. The customer cannot open
    // one, so it is a dead link at best and a login prompt at worst.
    //
    // Every other link STAYS, whole. An answer that says "pull the current
    // list from config.zscaler.com/..." is giving the customer the one thing
    // they need to act, and this function's job is to remove attribution, not
    // to take the answer apart. An earlier version kept only the label text
    // and dropped the href, which silently turned working links into flat
    // words — worse than removing them, because it still looked like a link
    // had been offered.
    //
    // Judged by the URL alone, never by extension: a .pdf on a public docs
    // site is ordinary content, and only `isServerProxyDocUrl` knows which
    // host is ours.
    // Images first - ![alt](url) is a link too, so the link rule below would
    // eat the "!" and strand the alt text.
    .replace(/!\[[^\]]*\]\(([^)]*)\)/g, (m, url) => (isServerProxyDocUrl(url) ? "" : m))
    .replace(/\[([^\]]*)\]\(([^)]*)\)/g, (m, label, url) =>
      (isServerProxyDocUrl(url) ? "" : m))
    // Bare internal URLs, same reasoning, with no label to preserve.
    .replace(/\bhttps?:\/\/\S*\/api\/(?:documents|images)\/\S*/gi, "")
    // Pills sit at the end of a clause, so removing one strips its leading
    // space too and leaves " ." or a double space behind.
    .replace(/[ \t]+([.,;:!?])/g, "$1")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/[ \t]+$/gm, "")
    .trim();
}

// [S1], [S1][S3], [S1, S2] — inline attribution the agent puts at the end of
// every synthesised block.
const CITE_RE = /\[\s*S\d+(?:\s*,\s*S\d+)*\s*\](?:\s*\[\s*S\d+(?:\s*,\s*S\d+)*\s*\])*/gi;

const escapeAttr = (s = "") => String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * Turn the agent's [S1][S2] markers into numbered source pills. Each pill
 * carries the id so a click can open that source and highlight its card.
 */
function renderCitationPills(text, sources = []) {
  const byId = new Map();
  sources.forEach((s, i) => {
    const id = String(s.id || `S${i + 1}`).toUpperCase();
    // `n` is the session-stable number the server assigned, so [1] refers to the
    // same document on turn 6 as it did on turn 1. Falls back to position.
    byId.set(id, { index: i, number: s.n || i + 1, title: s.title || "" });
  });

  return text.replace(CITE_RE, (match) => {
    const ids = (match.match(/S\d+/gi) || []).map((id) => id.toUpperCase());
    const pills = ids
      .map((id) => {
        const meta = byId.get(id);
        // Index falls back to the id's own number so pills still work before
        // the enriched sources land (or if a card was trimmed by the top-5 cap).
        const index = meta ? meta.index : Number(id.slice(1)) - 1;
        const label = meta ? meta.number : index + 1;
        const title = meta?.title ? `${label}. ${meta.title}` : `Source ${label}`;
        return `<span role="button" tabindex="0" class="d5-cite-pill" data-cite-id="${id}" data-cite-index="${index}" title="${escapeAttr(title)}">${label}</span>`;
      })
      .join("");
    return pills ? `<span class="d5-cite-group">${pills}</span>` : "";
  });
}

export function formatBotMessage(text, sources = []) {
  if (!text) return "";
  let t = String(text)
    .replace(MANIFEST_RE, "")
    .replace(/^\s+/, "")
    .split("\n")
    .filter((l) => !/^\*\*Note\s*[-:]/.test(l.trim()) && !/^Note\s*:/i.test(l.trim()) && !/^\*\*TL;?DR\*?\*?/i.test(l.trim()))
    .join("\n");

  // ── fenced / inline code ── park behind placeholders before any other transform.
  const codeBlocks = [];
  const escapeHtml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const park = (html, kind) => {
    codeBlocks.push(html);
    return `@@CODE${kind}${codeBlocks.length - 1}@@`;
  };
  t = t.replace(/```[ \t]*([a-zA-Z0-9_+-]*)[ \t]*\r?\n([\s\S]*?)```/g, (_m, _lang, code) =>
    park(
      '<pre class="d5-code-block"><code>' + escapeHtml(code.replace(/\s+$/, "")) + "</code></pre>",
      "B"
    )
  );
  // unterminated fence (streamed/truncated message)
  t = t.replace(/```[ \t]*([a-zA-Z0-9_+-]*)[ \t]*\r?\n([\s\S]*)$/g, (_m, _lang, code) =>
    park(
      '<pre class="d5-code-block"><code>' + escapeHtml(code.replace(/\s+$/, "")) + "</code></pre>",
      "B"
    )
  );
  t = t.replace(/`([^`\n]+)`/g, (_m, code) =>
    park('<code class="d5-code-inline">' + escapeHtml(code) + "</code>", "I")
  );

  // An unterminated manifest — a message cut off mid-block — would otherwise
  // render its raw rows as prose. Strip to end of string, but NEVER when that
  // would erase the whole message: the agent often emits the manifest FIRST
  // (observed: inform_user starting with "::sources\nS1 | docs | ..."), and a
  // leading unterminated block made this swallow the entire answer, so a search
  // the server had already sent showed up as nothing at all. In that case drop
  // only the manifest's own lines and keep the prose after it.
  const withoutTail = t.replace(/^[ \t]*::sources[ \t]*\r?\n[\s\S]*$/im, "");
  if (withoutTail.trim()) {
    t = withoutTail;
  } else {
    t = t
      .replace(/^[ \t]*::(?:sources|end)[ \t]*$/gim, "")
      .replace(/^[ \t]*S\d+[ \t]*\|.*$/gim, "")
      .trim();
  }
  // Escaped before any HTML is built (code blocks were escaped when parked).
  t = escapeProse(t);
  // Pills go in after code parking so an [S1] inside a code sample stays literal.
  t = renderCitationPills(t, sources);

  // The alt is the anchor's only visible text, and the markdown a knowledge-graph
  // image arrives as is `![][URL]` — no alt at all. That produced an anchor with
  // nothing inside it: present in the DOM, zero pixels wide, so the image looked
  // like it had been dropped. Fall back to a label instead of depending on the
  // model to supply one.
  t = t.replace(
    MD_IMAGE_RE,
    (_m, alt, url) =>
      `<a tabindex="0" class="image-link d5-link" data-image-url="${url}">${alt || IMAGE_LINK_LABEL}</a>`
  );
  t = t.replace(/!\[\]\[?(https?:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)\/view[^\]]*)\]?/g, '<a tabindex="0" class="image-link d5-link" data-image-url="https://drive.google.com/file/d/$2/view?usp=sharing">image</a>');
  t = t.replace(/\[image\]\((https?:\/\/[^)]+)\)/g, '<a tabindex="0" class="image-link d5-link" data-image-url="$1">image</a>');
  t = t.replace(/^#{1,3}\s+/gm, "");
  t = t.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  t = t.replace(/\*(.*?)\*/g, "$1");
  t = t.replace(/^(\d+)\.\s+/gm, '<span class="d5-list-num">$1.</span> ');
  t = t.replace(/^\s*[-•]\s+/gm, '<span class="d5-list-bullet">•</span> ');
  t = t.replace(MD_LINK_RE, (_, label, url) => {
    if (isImagePreviewLink(label, url)) return `<a tabindex="0" class="image-link d5-link" data-image-url="${url}">${label}</a>`;
    if (isServerProxyDocUrl(url)) return `<a tabindex="0" class="citation-link d5-link" data-doc-url="${url}">${label}</a>`;
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="d5-link">${label}</a>`;
  });
  t = t.replace(/(<strong>Source:<\/strong>\s*)(https?:\/\/[^\s<]+)/g, '$1<a href="$2" target="_blank" rel="noopener noreferrer" class="d5-link">Source Document</a>');
  const urlRegex = /(?<!")\b(https?:\/\/(?:[^\s<>)"&]|&(?!lt;|gt;|quot;))+)(?!")/gi;
  t = t.replace(urlRegex, (m) => `<a href="${m}" target="_blank" rel="noopener noreferrer" class="d5-link">${m}</a>`);
  t = t.replace(/(\|.+\|(?:\n\|[:\s\-|]+\|)?(?:\n\|.+\|)+)/g, (match) => {
    const lines = match.trim().split("\n").filter((l) => l.trim());
    if (lines.length < 2) return match;
    // Wrapped in a scroll container: a bare <table> is not a scroll container,
    // so a wide table pushes the whole page sideways.
    let html = '<div class="d5-table-wrap"><table class="d5-table"><thead><tr>';
    lines[0].split("|").filter((c) => c.trim()).forEach((c) => { html += `<th>${c.trim()}</th>`; });
    html += "</tr></thead><tbody>";
    for (let i = 2; i < lines.length; i++) {
      html += "<tr>";
      lines[i].split("|").filter((c) => c.trim()).forEach((c) => { html += `<td>${c.trim()}</td>`; });
      html += "</tr>";
    }
    return html + "</tbody></table></div>";
  });
  t = t.replace(
    /<strong>(What's Happening|Root Cause|Next Steps|Escalation Reality|Sources Selected|Recommended Path|Alternate Routes|Answer|Diagnosis Plan|My Pick:[^<]*|Recommended:[^<]*)<\/strong>/g,
    '<div class="d5-section-label">$1</div>'
  );
  t = t.replace(/^(Diagnostic Procedure|Current State|Current Position|Interpretation|Next Step|After This|Root Cause|Available Fixes|Workaround Options|Permanent Fix|Important|What we are checking|Steps|What to Observe|Next|ROOT CAUSE|Resolution Options|Recommended immediate action):/gm, "<strong>$1:</strong>");
  t = t.replace(/^Response Type:.*$/gm, "");
  t = t.replace(/^---$/gm, '<hr class="d5-hr">');
  t = t.replace(/\n{3,}/g, "\n\n");
  t = t.replace(/\n/g, "<br>");
  t = t.replace(/(<br>\s*){3,}/g, "<br><br>");
  // Block-level elements bring their own margins; the <br>s that survive around
  // them stack on top of that and open a large hole in the message.
  t = t.replace(/(<br>\s*)+(<hr class="d5-hr">)(\s*<br>)*/g, "$2");
  t = t.replace(/(<br>\s*)+(<div class="d5-section-label">)/g, "$2");
  t = t.replace(/(<\/div>)(\s*<br>)+/g, "$1");
  t = t.replace(/(<br>\s*)+(<div class="d5-table-wrap">)/g, "$2");

  if (codeBlocks.length) {
    t = t.replace(/(?:<br>)?\s*@@CODEB(\d+)@@\s*(?:<br>)?/g, (m, i) => codeBlocks[Number(i)] ?? m);
    t = t.replace(/@@CODEI(\d+)@@/g, (m, i) => codeBlocks[Number(i)] ?? m);
  }
  return t;
}

/**
 * Which sources this message actually cited, as session-stable numbers — drives
 * the green tick. Reads the same [S1] markers the pills are built from, so a
 * card is only ticked when the answer really leaned on it. Numbers rather than
 * array positions, because the card row only renders first-sighting sources and
 * positions would no longer line up.
 */
export function citedSourceNumbers(text, sources = []) {
  const cited = new Set();
  if (!text) return cited;
  const byId = new Map();
  sources.forEach((s, i) => byId.set(String(s.id || `S${i + 1}`).toUpperCase(), s.n || i + 1));
  // Code samples can contain bracketed text that looks like a citation; a tick
  // must only come from real prose attribution.
  const prose = String(text).replace(/```[\s\S]*?```/g, "").replace(/`[^`\n]*`/g, "");
  for (const raw of prose.match(CITE_RE) || []) {
    for (const id of raw.match(/S\d+/gi) || []) {
      const key = id.toUpperCase();
      // A stray id that maps to no source is not a citation — ignore it rather
      // than ticking a card the answer never leaned on.
      if (byId.has(key)) cited.add(byId.get(key));
    }
  }
  return cited;
}

export const formatTimestamp = (ts) =>
  ts ? new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";
